import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { useParams, useNavigate, generatePath } from 'react-router';
import { unwrapResult } from '@reduxjs/toolkit';
import { camelCase } from 'change-case';
import { useAppDispatch } from '@/store';
import {
  SubscriptionRequest,
  translateApiErrors,
} from '@/types/subscriptions/request';
import { RootState } from '@/store';
import { apiStore } from '@/store/api';
import { errorStore } from '@/store/error';
import { organizationSubscriptionStore } from '@/store/organization/subscription';
import { LoaderWithDimmer } from '@/components/utils/LoaderWithDimmer';
import { CenteredLoader } from '@/components/utils/CenteredLoader';
import { Input } from '../Presentation/Input';
import { Confirm } from '../Presentation/Confirm';
import { DoneContent } from '@/components/pages/Subscription/Presentation/DoneContent';
import { SubscriptionForm } from '@/components/pages/Subscription/Container';
import { Preview } from '../../Presentation/Preview';
import { Done } from '../Presentation/Done';
import { useStep, Step } from '@/hooks/useStep';
import { pages } from '@/settings/pages';
import { SERVICE_GROUP_ID } from '@/constants';
import i18n from '@/settings/i18n';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { resetErrorState } from '@/store/error/reducer';

const getIsConnecting = apiStore.selectors.createConnectingSelector([
  organizationSubscriptionStore.asyncActions.create.typePrefix,
]);
const getCreateSubscriptionError = errorStore.selectors.createErrorSelector(
  organizationSubscriptionStore.asyncActions.create.typePrefix,
);

const selector = (state: RootState) => {
  const { api, organization } = state;
  const e = getCreateSubscriptionError(state.error);

  let translatedErrors = {};
  if (e && e.data && e.status === 422) {
    const { errors } = e.data;
    translatedErrors = translateApiErrors(errors, organization.service.items);
  }
  return {
    isConnecting: getIsConnecting(api),
    subscription: organization.subscription.item,
    warnings: organization.subscription.warnings,
    createSubscriptionError: translatedErrors,
  };
};

const contactIdSelector = (state: RootState) => {
  const items = state.organization.contact.items;
  const item = items[0];
  const id = item !== undefined ? item.id : 0;
  const lastItem = items[items.length - 1];

  return {
    contactId: id,
    defaultContactId: lastItem ? lastItem.id : undefined,
  };
};

const billingContactIdSelector = (state: RootState) => {
  const items = state.organization.billingContact.items;
  const item = items[0];
  const id = item !== undefined ? item.id : 0;
  const lastItem = items[items.length - 1];

  return {
    billingContactId: id,
    defaultBillingContactId: lastItem ? lastItem.id : undefined,
  };
};

export const SubscriptionNewContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const { isConnecting, subscription, warnings, createSubscriptionError } =
    useSelector(selector);
  const { contactId, defaultContactId } = useSelector(contactIdSelector);
  const { billingContactId, defaultBillingContactId } = useSelector(
    billingContactIdSelector,
  );
  const dispatch = useAppDispatch();

  const [subscriptionRequest, setSubscriptionRequest] =
    useState<SubscriptionRequest>({
      projectClassId: undefined,
      adminIds: [],
      serviceIds: [],
      startsOn: '',
      users: [],
      billingContactId: 0,
      contactId: 0,
    });
  const [isInitialized, setIsInitialized] = useState(false);
  const { step, stepTo } = useStep();

  const navigate = useNavigate();
  const params = useParams<{ serviceGroupId: string }>();
  const serviceGroupId = Number(params.serviceGroupId);
  const generateStepUrl = useCallback(
    (serviceGroupId: number) => {
      return `/subscription/group/${serviceGroupId}/new/${camelCase(
        Step[step],
      )}`;
    },
    [step],
  );

  useEffect(() => {
    return () => {
      dispatch(resetErrorState());
    };
  }, [dispatch]);

  const navigateToTopTransferredService = useCallback(() => {
    if (serviceGroupId !== SERVICE_GROUP_ID.emba) {
      flashError(i18n.t('ui.error.unexpected'));
      navigate(generatePath(pages.service.detail, { id: serviceGroupId }));
    }
  }, [flashError, navigate, serviceGroupId]);

  navigateToTopTransferredService();

  useEffect(() => {
    (async () => {
      await dispatch(
        organizationSubscriptionStore.asyncActions.initializeSubscriptionNew({
          serviceGroupId,
        }),
      ).then(unwrapResult);

      setIsInitialized(true);
    })();

    return () => {
      dispatch(organizationSubscriptionStore.actions.clearItemAndWarnings());
    };
  }, [dispatch, serviceGroupId]);

  useEffect(() => {
    // GTM発火用pushState
    // stepの変更でのみ発火してほしいのでdepsはstepのみ
    window.history.pushState(
      { step: Step[step] },
      document.title,
      generateStepUrl(serviceGroupId),
    );
  }, [generateStepUrl, serviceGroupId, step]);

  const onSubmitDryRun = useCallback(
    async (subscriptionRequest: SubscriptionRequest) => {
      navigateToTopTransferredService();
      setSubscriptionRequest(subscriptionRequest);
      await dispatch(
        organizationSubscriptionStore.asyncActions.create({
          subscriptionRequest,
          dryRun: true,
        }),
      ).then(unwrapResult);

      // スクロール位置制御のためハッシュ値をクリア
      navigate(
        {
          pathname: window.location.pathname,
          hash: '',
        },
        { replace: true },
      );
      stepTo.confirm();
    },
    [dispatch, navigate, navigateToTopTransferredService, stepTo],
  );

  const onSubmit = useCallback(async () => {
    navigateToTopTransferredService();
    await dispatch(
      organizationSubscriptionStore.asyncActions.create({
        subscriptionRequest,
        dryRun: false,
      }),
    ).then(unwrapResult);
    stepTo.done();
  }, [dispatch, navigateToTopTransferredService, stepTo, subscriptionRequest]);

  if (!isInitialized) {
    return <CenteredLoader />;
  }

  return (
    <>
      {isConnecting && (
        <LoaderWithDimmer>
          <Trans i18nKey="ui.subscription.spinner">
            <br />
          </Trans>
        </LoaderWithDimmer>
      )}
      {(() => {
        switch (step) {
          case Step.Input:
            return (
              <Input>
                <SubscriptionForm
                  serviceGroupId={serviceGroupId}
                  values={subscriptionRequest}
                  extraErrors={createSubscriptionError}
                  billingContactId={billingContactId}
                  contactId={contactId}
                  defaultContactId={defaultContactId}
                  defaultBillingContactId={defaultBillingContactId}
                  onSubmit={onSubmitDryRun}
                />
              </Input>
            );
          case Step.Confirm:
            return (
              <Confirm>
                {subscription && (
                  <Preview
                    mode="new"
                    isConnecting={isConnecting}
                    subscription={subscription}
                    onBack={stepTo.input}
                    onSubmit={onSubmit}
                    warnings={warnings}
                  />
                )}
              </Confirm>
            );
          case Step.Done:
            return (
              <Done>
                {subscription && <DoneContent subscription={subscription} />}
              </Done>
            );
        }
      })()}
    </>
  );
};
